import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import UiButton from '@components/ui/button/UiButton';
import { Col, DatePicker, Form, Input, Select } from 'antd';
import { ApplicantElement, PatchApplicantModel } from '@models/applicants';
import DownCaretArrow from '@assets/svgs/down-caret-arrow-icon.svg';
import dayjs from 'dayjs';
import { patchCandidate } from '@services/applicants/applicants-http.service';
import useNotification from '@hooks/notification/useNotification';
import StateEnum from '@constants/state.enum';
import { LookupType } from '@models/lookup';
import { getAppLookup } from '@services/lookup/lookup-http.service';
import { useTranslation } from 'next-i18next';
import { changeDateFormat } from '@services/date/date-format';
import styles from './ApplicantData.module.scss';
import emailRegex from '@constants/regex';

const { Option } = Select;
require('dayjs/locale/el');

dayjs.locale('el');

type ApplicantDataProps = {
  applicant: ApplicantElement;
  fetchApplicant: () => void;
  fetchApplicants?: () => void;
};
const ApplicantData: React.FC<ApplicantDataProps> = ({ applicant, fetchApplicant, fetchApplicants }) => {
  const { t: translate, i18n } = useTranslation('applicant');
  const [form] = Form.useForm();
  const [editFormState, setEditFormState] = useState<'edit' | 'view'>('view');
  const [loading, setLoading] = useState<boolean>(false);
  const [updatedCandidate, setUpdatedCandidate] = useState<ApplicantElement>(applicant);
  const [educationLevel, setEducationLevel] = useState<LookupType>([]);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [phoneValidation, setPhoneValidation] = useState<boolean>(false);
  const [mobileValidation, setMobileValidation] = useState<boolean>(false);
  const [emailValidation, setEmailValidation] = useState<boolean>(false);

  const [generalError] = useNotification(
    StateEnum.ERROR,
    'Something went wrong',
    'Oops, something went wrong, please try again later!',
  );

  const applicantDateOfBirth = updatedCandidate?.dateOfBirth ?? applicant.dateOfBirth ?? null;

  const candidateFieldsView: Array<{ title: string; description: string | undefined }> = [
    {
      title: translate('name'),
      description: updatedCandidate?.firstName || applicant?.firstName,
    },
    {
      title: translate('last_name'),
      description: updatedCandidate?.lastName || applicant?.lastName,
    },
    {
      title: translate('email'),
      description: updatedCandidate?.email || applicant?.email,
    },
    {
      title: translate('mobile'),
      description: updatedCandidate?.mobilePhoneNumber || applicant?.mobilePhoneNumber,
    },
    {
      title: translate('phone'),
      description: updatedCandidate?.phoneNumber || applicant.phoneNumber,
    },
    {
      title: translate('linkedIn'),
      description: updatedCandidate?.linkedin || applicant.linkedin,
    },
    {
      title: translate('residential'),
      description: updatedCandidate?.residentialArea || applicant.residentialArea,
    },
    {
      title: translate('address'),
      description: updatedCandidate?.address || applicant.address,
    },
    {
      title: translate('number'),
      description: updatedCandidate?.addressNumber || applicant.addressNumber,
    },
    {
      title: translate('post_code'),
      description: updatedCandidate?.postCode || applicant.postCode,
    },
    {
      title: translate('dob'),
      description: applicantDateOfBirth ? changeDateFormat(applicantDateOfBirth) : '—',
    },
    {
      title: translate('education'),
      description: updatedCandidate?.educationLevel || applicant.educationLevel,
    },
  ];

  const handleIsDisabled = () => {
    const firstNameField = form.getFieldValue('firstName').trimStart();
    const lastNameField = form.getFieldValue('lastName').trimStart();

    setIsDisabled(!firstNameField || !lastNameField);
  };

  const ViewState: ReactNode = (
    <>
      {candidateFieldsView.map((infoPair: { title: string; description: string | undefined }) => {
        return (
          <Col span={8} key={infoPair.title}>
            <p className={`h6 ${styles.infoTitle}`}>{infoPair.title}</p>
            {infoPair.description ? (
              <p className={`small-body-text ${styles.infoDescription}`}>{infoPair.description}</p>
            ) : (
              <p className={`small-body-text ${styles.infoDescription}`}>—</p>
            )}
          </Col>
        );
      })}
    </>
  );

  const onFinish = async (values: PatchApplicantModel) => {
    setLoading(true);
    const updateCandidateFields: PatchApplicantModel = {
      firstName: values.firstName?.trimStart(),
      lastName: values.lastName?.trimStart(),
      email: values.email?.trimStart(),
      mobilePhoneNumber: values.mobilePhoneNumber?.trimStart(),
      phoneNumber: values.phoneNumber?.trimStart(),
      residentialArea: values.residentialArea?.trimStart(),
      address: values.address?.trimStart(),
      addressNumber: values.addressNumber?.trimStart(),
      postCode: values.postCode?.trimStart(),
      dateOfBirth: values.dateOfBirth,
      educationLevel: values.educationLevel,
      linkedin: values.linkedin?.trimStart(),
    };
    if (applicant.id) {
      try {
        const updateCandidate = await patchCandidate(updateCandidateFields, applicant.id.toString());
        setUpdatedCandidate(updateCandidate);
        setLoading(false);
        setEditFormState('view');
        await fetchApplicant();
        if (fetchApplicants) await fetchApplicants();
      } catch (e: any) {
        generalError();
        setLoading(false);
      }
    }
  };

  const fetchSelectLookup = useCallback(
    async (name: string) => {
      try {
        const lookupItems: LookupType = await getAppLookup(name, i18n.language);
        if (name === 'educationLevel') {
          setEducationLevel(lookupItems);
        }
      } catch {
        generalError();
      }
    },
    [editFormState],
  );

  useEffect(() => {
    if (editFormState === 'edit') {
      handleIsDisabled();
      fetchSelectLookup('educationLevel');
    }
  }, [editFormState]);

  const phoneNumberRegex = /^(\+[0-9][0-9])?[0-9]{10}$/;

  const validatePhoneNumber = (_: any, value: String) => {
    if (!value || value.match(phoneNumberRegex)) {
      setPhoneValidation(false);
      return Promise.resolve();
    }
    if (!value.match(phoneNumberRegex)) {
      setPhoneValidation(true);
      return Promise.reject(translate('phone_number_validation_error'));
    }

    return Promise.resolve();
  };

  const validateMobileNumber = (_: any, value: String) => {
    if (!value || value.match(phoneNumberRegex)) {
      setMobileValidation(false);
      return Promise.resolve();
    }
    if (!value.match(phoneNumberRegex)) {
      setMobileValidation(true);
      return Promise.reject(translate('phone_number_validation_error'));
    }

    return Promise.resolve();
  };

  const validateEmail = (_: any, value: String) => {
    if (!value || value.match(emailRegex)) {
      setEmailValidation(false);
      return Promise.resolve();
    }
    if (!value.match(emailRegex)) {
      setEmailValidation(true);
      return Promise.reject('Please enter a valid email.');
    }

    return Promise.resolve();
  };

  const EditState: ReactNode = (
    <div className={styles.editState}>
      <Form name="editCompany" onFinish={onFinish} layout="inline" className="antd-overwrite" form={form}>
        <Col span={8}>
          <p className={`h6 ${styles.infoTitle}`}>
            <div className={styles.asterisk}>*</div>
            {translate('name')}
          </p>
          <Form.Item
            name="firstName"
            className={styles.infoField}
            initialValue={applicant.firstName}
            required
            rules={[{ required: true, message: translate('first_name_error_message') }]}
          >
            <Input
              size="small"
              disabled={loading}
              maxLength={50}
              onChange={() => handleIsDisabled()}
              data-testid="first-name-input"
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <p className={`h6 ${styles.infoTitle}`}>
            <div className={styles.asterisk}>*</div>
            {translate('last_name')}
          </p>
          <Form.Item
            name="lastName"
            className={styles.infoField}
            initialValue={applicant.lastName}
            rules={[{ required: true, message: translate('last_name_error_message') }]}
          >
            <Input
              size="small"
              disabled={loading}
              maxLength={50}
              onChange={() => handleIsDisabled()}
              data-testid="last-name-input"
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <p className={`h6 ${styles.infoTitle}`}>{translate('email')}</p>
          <Form.Item
            name="email"
            className={styles.infoField}
            initialValue={applicant.email}
            rules={[{ validator: validateEmail }]}
          >
            <Input size="small" disabled={loading} maxLength={150} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <p className={`h6 ${styles.infoTitle}`}>{translate('mobile')}</p>
          <Form.Item
            name="mobilePhoneNumber"
            className={styles.infoField}
            initialValue={applicant.mobilePhoneNumber && applicant.mobilePhoneNumber}
            rules={[{ validator: validateMobileNumber }]}
          >
            <Input size="small" disabled={loading} maxLength={13} data-testid="mobile-number-input" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <p className={`h6 ${styles.infoTitle}`}>{translate('phone')}</p>
          <Form.Item
            name="phoneNumber"
            className={styles.infoField}
            initialValue={applicant.phoneNumber && applicant.phoneNumber}
            rules={[{ validator: validatePhoneNumber }]}
          >
            <Input size="small" disabled={loading} maxLength={13} data-testid="phone-input" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <p className={`h6 ${styles.infoTitle}`}>{translate('linkedIn')}</p>
          <Form.Item name="linkedin" className={styles.infoField} initialValue={applicant.linkedin}>
            <Input size="small" disabled={loading} maxLength={600} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <p className={`h6 ${styles.infoTitle}`}>{translate('residential')}</p>
          <Form.Item name="residentialArea" className={styles.infoField} initialValue={applicant.residentialArea}>
            <Input size="small" disabled={loading} maxLength={50} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <p className={`h6 ${styles.infoTitle}`}>{translate('address')}</p>
          <Form.Item name="address" className={styles.infoField} initialValue={applicant.address}>
            <Input size="small" disabled={loading} maxLength={200} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <p className={`h6 ${styles.infoTitle}`}>{translate('number')}</p>
          <Form.Item name="addressNumber" className={styles.infoField} initialValue={applicant.addressNumber}>
            <Input size="small" disabled={loading} maxLength={600} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <p className={`h6 ${styles.infoTitle}`}>{translate('post_code')}</p>
          <Form.Item name="postCode" className={styles.infoField} initialValue={applicant.postCode}>
            <Input size="small" disabled={loading} maxLength={200} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <p className={`h6 ${styles.infoTitle}`}>{translate('dob')}</p>
          <Form.Item name="dateOfBirth" className={styles.infoField}>
            <DatePicker
              value={dayjs(applicant.dateOfBirth)}
              defaultValue={applicant.dateOfBirth ? dayjs(applicant.dateOfBirth) : undefined}
              format="DD MMM YYYY"
              size="small"
              showToday={false}
              disabledDate={(current) => current.isAfter(dayjs().subtract(17, 'year'))}
              placeholder={translate('select_date_placeholder')}
              defaultPickerValue={dayjs().subtract(17, 'year')}
              inputReadOnly
              disabled={loading}
              onChange={async (value) => {
                await form.setFieldsValue({ dateOfBirth: value });
              }}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <p className={`h6 ${styles.infoTitle}`}>{translate('education')}</p>
          <Form.Item key="educationLevel" name="educationLevel" className={styles.infoField}>
            <Select
              size="large"
              suffixIcon={<DownCaretArrow />}
              placeholder={<span className="small-body-text">{translate('please_select')}</span>}
              optionFilterProp="children"
              defaultValue={applicant.educationLevel}
              onChange={async (value) => {
                await form.setFieldsValue({ [`educationLevel`]: value });
              }}
            >
              {educationLevel.map((optionValue) => {
                return (
                  <Option key={optionValue.id} value={optionValue.id}>
                    {optionValue.name}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
        </Col>
      </Form>
      <div className={styles.editActionButtons}>
        <UiButton
          data-testid="update-button"
          type="primary"
          size="small"
          htmlType="submit"
          onClick={() => onFinish(form.getFieldsValue())}
          disabled={emailValidation || phoneValidation || mobileValidation || isDisabled}
        >
          {translate('update_save')}
        </UiButton>
        <UiButton
          data-testid="cancel-button"
          size="small"
          onClick={() => {
            form.resetFields();
            setEditFormState('view');
          }}
        >
          {translate('cancel')}
        </UiButton>
      </div>
    </div>
  );

  return (
    <div className={styles.wrapDataElem}>
      <div className={styles.wrapTitle}>
        <div className={`h5 ${styles.dataTitle}`}>{translate('personal_info')}</div>
        {editFormState === 'view' && (
          <UiButton data-testid="main-update-button" size="small" onClick={() => setEditFormState('edit')}>
            {translate('update')}
          </UiButton>
        )}
      </div>
      <div className={styles.wrapData}>{editFormState === 'view' ? ViewState : EditState}</div>
    </div>
  );
};

export default ApplicantData;
